'use client';

import { usePathname, useSearchParams } from 'next/navigation';
import { useCallback } from 'react';

import { StorageKeys } from 'src/constants';

interface DetailBaseEntry {
  pathname: string;
  search: string;
}

/**
 * useDetailHistory - 管理详情页的历史记录
 *
 * 只保留底层（列表页）记录，每次打开新详情页时替换当前详情页
 * 点击返回时回到底层
 *
 * @example
 * ```tsx
 * 在链接组件中保存底层
 * const history = useDetailHistory();
 * <a onClick={() => history.saveBase()}>查看详情</a>
 *
 * 在返回按钮中回到底层
 * const history = useDetailHistory();
 * <button onClick={() => history.backToBase('/default')}>返回</button>
 * ```
 */
export function useDetailHistory() {
  const pathname = usePathname();
  const searchParams = useSearchParams();

  /**
   * 检查当前是否在详情页中
   */
  const isInDetailPage = useCallback((): boolean => {
    const detailPatterns = ['/station/', '/vehicle/', '/ticket/'];
    return detailPatterns.some((pattern) => pathname.startsWith(pattern));
  }, [pathname]);

  /**
   * 保存当前页面作为底层（列表页）
   * 只在不在详情页时保存，如果已在详情页则不保存
   */
  const saveBase = useCallback(() => {
    try {
      // 如果当前已经在详情页中，不保存（避免详情页覆盖真正的底层）
      if (isInDetailPage()) {
        return;
      }

      const base: DetailBaseEntry = {
        pathname,
        search: searchParams.toString(),
      };

      // 保存当前页面作为底层
      sessionStorage.setItem(StorageKeys.detailHistory, JSON.stringify(base));
    } catch (error) {
      console.error('Failed to save detail base:', error);
    }
  }, [pathname, searchParams, isInDetailPage]);

  /**
   * 返回到底层页面
   *
   * @param defaultPath - 如果没有保存的底层时使用的默认路径
   * @returns 要跳转的完整 URL，如果没有底层则返回 defaultPath
   */
  const backToBase = useCallback((defaultPath: string): string => {
    try {
      const baseJson = sessionStorage.getItem(StorageKeys.detailHistory);

      if (baseJson) {
        const base: DetailBaseEntry = JSON.parse(baseJson);
        // 清除底层记录
        sessionStorage.removeItem(StorageKeys.detailHistory);
        // 返回完整 URL
        return base.pathname + (base.search ? `?${base.search}` : '');
      }

      return defaultPath;
    } catch (error) {
      console.error('Failed to back to base:', error);
      return defaultPath;
    }
  }, []);

  /**
   * 清空底层记录
   */
  const clear = useCallback(() => {
    try {
      sessionStorage.removeItem(StorageKeys.detailHistory);
    } catch (error) {
      console.error('Failed to clear detail history:', error);
    }
  }, []);

  /**
   * 检查是否有保存的底层记录
   */
  const hasBase = useCallback((): boolean => {
    try {
      const baseJson = sessionStorage.getItem(StorageKeys.detailHistory);
      return !!baseJson;
    } catch (error) {
      console.error('Failed to check detail history:', error);
      return false;
    }
  }, []);

  return {
    saveBase,
    backToBase,
    clear,
    hasBase,
  };
}
